import { createRouter, createWebHistory } from 'vue-router'

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomePage.vue'),
    meta: {
      title: 'SecondChance.dev - A New Beginning for Tech Talent',
      description: 'The bias-free hiring platform where your past doesn\'t dictate your future. Connect with opportunities, mentors, and AI upskilling programs. Skills over resumes, potential over credentials.'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/AboutPage.vue'),
    meta: {
      title: 'About - SecondChance.dev',
      description: 'Learn about our mission to unlock the full potential of overlooked tech talent through bias-free hiring, AI upskilling, and mentorship programs that focus on skills and potential.'
    }
  },
  {
    path: '/for-talent',
    name: 'for-talent',
    component: () => import('@/views/ForTalentPage.vue'),
    meta: {
      title: 'For Talent - SecondChance.dev',
      description: 'Launch your tech career with bias-free opportunities, AI career assessment, skills-based hiring, and mentorship. No resume required - your skills and potential matter most.'
    }
  },
  {
    path: '/for-employers',
    name: 'for-employers',
    component: () => import('@/views/ForEmployersPage.vue'),
    meta: {
      title: 'For Employers - SecondChance.dev',
      description: 'Hire diverse, motivated tech talent through bias-free assessment. Access overlooked candidates, reduce hiring costs by 40%, and build inclusive teams that drive innovation.'
    }
  },
  {
    path: '/how-it-works',
    redirect: '/for-talent'
  },
  {
    path: '/programs',
    name: 'programs',
    component: () => import('@/views/ProgramsPage.vue'),
    meta: {
      title: 'Programs - SecondChance.dev',
      description: 'Three AI-First programs to take you from learning to launching. AI Skills Academy, AI Incubator, and AI Upskilling — all free for qualifying participants.'
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/views/ContactPage.vue'),
    meta: {
      title: 'Contact - SecondChance.dev',
      description: 'Get in touch with SecondChance.dev to start your journey or learn more.'
    }
  },
  {
    path: '/ai-upskilling',
    name: 'ai-upskilling',
    component: () => import('@/views/AIUpskillingPage.vue'),
    meta: {
      title: 'AI Upskilling - SecondChance.dev',
      description: 'Master AI skills with our comprehensive curriculum for candidates, mentors, and employers. Three specialized pathways: 12-week candidate program, 6-week mentor training, 8-week employer transformation.'
    }
  },
  {
    path: '/ai-incubator',
    name: 'ai-incubator',
    component: () => import('@/views/AIIncubatorPage.vue'),
    meta: {
      title: 'AI Incubator - SecondChance.dev',
      description: 'A mentorship-driven program for aspiring entrepreneurs to learn AI-First development and launch real AI-powered products. Cohort-based, remote, free for qualifying participants.'
    }
  },
  {
    path: '/ai-skills-academy',
    name: 'ai-skills-academy',
    component: () => import('@/views/AISkillsAcademyPage.vue'),
    meta: {
      title: 'AI Skills Academy - SecondChance.dev',
      description: 'A 6-week remote course in AI-First development. No prerequisites, no degree required. Learn prompt engineering, RAG, agents, and full-stack AI — and build real portfolio projects.'
    }
  },
  {
    path: '/freelance-u',
    name: 'freelance-u',
    component: () => import('@/views/FreelanceUPage.vue'),
    meta: {
      title: 'Freelance U - SecondChance.dev',
      description: 'Turn your AI skills into freelance income in 10 weeks. A career program for anyone — inside or outside tech — who wants to work on their own terms in the AI economy. No CS degree required.'
    }
  },
  {
    path: '/for-mentors',
    name: 'for-mentors',
    component: () => import('@/views/ForMentorsPage.vue'),
    meta: {
      title: 'For Mentors - SecondChance.dev',
      description: 'Become an AI career mentor and guide overlooked talent into tech careers. Join our 6-week mentor training program and make a meaningful impact while developing leadership skills.'
    }
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('@/views/PrivacyPolicyPage.vue'),
    meta: {
      title: 'Privacy Policy - SecondChance.dev',
      description: 'Learn how SecondChance.dev collects, uses, and protects your personal information.'
    }
  },
  {
    path: '/privacy',
    redirect: '/privacy-policy'
  },
  {
    path: '/terms-of-service',
    name: 'terms-of-service',
    component: () => import('@/views/TermsOfServicePage.vue'),
    meta: {
      title: 'Terms of Service - SecondChance.dev',
      description: 'Terms and conditions for using the SecondChance.dev platform and services.'
    }
  },
  {
    path: '/terms',
    redirect: '/terms-of-service'
  },
  {
    path: '/blog',
    name: 'blog',
    component: () => import('@/views/BlogPage.vue'),
    meta: {
      title: 'Blog - SecondChance.dev',
      description: 'Insights, stories, and resources for building a bias-free tech career.'
    }
  },
  {
    path: '/blog/:slug',
    name: 'blog-article',
    component: () => import('@/views/BlogArticlePage.vue'),
    meta: {
      title: 'Article - SecondChance.dev',
      description: 'Read insights and stories about bias-free hiring and tech careers.'
    }
  },
  {
    path: '/resources',
    name: 'resources',
    component: () => import('@/views/ResourcesPage.vue'),
    meta: {
      title: 'Resources - SecondChance.dev',
      description: 'Organizations, initiatives, and resources championing inclusive hiring and second chances in tech.'
    }
  },
  {
    path: '/admin/partnerships',
    name: 'admin-partnerships',
    component: () => import('@/views/AdminPartnershipsPage.vue'),
    meta: {
      title: 'Partnership Inquiries - Admin',
      description: 'Manage corporate sponsorship and investor inquiries.',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/pledge',
    name: 'pledge',
    component: () => import('@/views/PledgePage.vue'),
    meta: {
      title: 'Inclusive Hiring Pledge - SecondChance.dev',
      description: 'Join forward-thinking employers who commit to skills-first, bias-free hiring. Sign the Inclusive Hiring Pledge and be listed in our public directory.'
    }
  },
  {
    path: '/employers/:slug',
    name: 'employer-profile',
    component: () => import('@/views/EmployerProfilePage.vue'),
    meta: {
      title: 'Employer Profile - SecondChance.dev',
      description: 'This employer has signed the Inclusive Hiring Pledge, committing to skills-first, bias-free hiring practices.'
    }
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('@/views/FAQPage.vue'),
    meta: {
      title: 'FAQ - SecondChance.dev',
      description: 'Frequently asked questions about SecondChance.dev and bias-free hiring.'
    }
  },
  // Authentication routes
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginPage.vue'),
    meta: {
      title: 'Login - SecondChance.dev',
      description: 'Sign in to your SecondChance.dev account.',
      requiresGuest: true
    }
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/auth/SignupPage.vue'),
    meta: {
      title: 'Sign Up - SecondChance.dev',
      description: 'Create your SecondChance.dev account and start your journey.',
      requiresGuest: true
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordPage.vue'),
    meta: {
      title: 'Forgot Password - SecondChance.dev',
      description: 'Reset your SecondChance.dev account password.',
      requiresGuest: true
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordPage.vue'),
    meta: {
      title: 'Reset Password - SecondChance.dev',
      description: 'Set a new password for your SecondChance.dev account.'
    }
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/auth/VerifyEmailPage.vue'),
    meta: {
      title: 'Verify Email - SecondChance.dev',
      description: 'Verify your email address to activate your account.'
    }
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/auth/AuthCallbackPage.vue'),
    meta: {
      title: 'Authentication - SecondChance.dev',
      description: 'Processing authentication...'
    }
  },
  // Profile — public (loads from localStorage session_id, no auth required)
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfilePage.vue'),
    meta: {
      title: 'My Profile - SecondChance.dev',
      description: 'Your SecondChance.dev profile — your skills story, next steps, and recommended path.'
    }
  },
  // Protected routes
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardPage.vue'),
    meta: {
      title: 'Dashboard - SecondChance.dev',
      description: 'Your SecondChance.dev dashboard.',
      requiresAuth: true
    }
  },
  {
    path: '/proposal',
    name: 'proposal',
    component: () => import('@/views/ProposalPage.vue'),
    meta: {
      title: 'Proposal Generator - SecondChance.dev',
      description: 'Paste a job, get a winning Upwork proposal in seconds.',
      requiresAuth: true
    }
  },
  {
    path: '/income',
    name: 'income',
    component: () => import('@/views/IncomePage.vue'),
    meta: {
      title: 'Income Tracker - SecondChance.dev',
      description: 'Track your freelance income milestones toward your first $1,000.',
      requiresAuth: true
    }
  },
  // ── Reentry entry point ───────────────────────────────────────────────
  // `bare: true` strips the site chrome (family bar, nav, footer) so this is
  // a single-path page. The URL is printed on posters in reentry offices and
  // must not change.
  {
    path: '/reentry',
    name: 'reentry',
    component: () => import('@/views/ReentryPage.vue'),
    meta: {
      bare: true,
      title: 'Back to Work in Tech — SecondChance.dev',
      description: 'For people who worked in tech, accounting, legal, IT or another professional field before incarceration. Free, remote AI upskilling, paid contract work, and introductions to skills-first employers. No background check, and we never ask about your conviction.'
    }
  },
  {
    path: '/reentry/verify',
    name: 'reentry-verify',
    component: () => import('@/views/ReentryVerifyPage.vue'),
    meta: {
      bare: true,
      title: 'Confirm your email - SecondChance.dev',
      description: 'Confirm your email address to complete your SecondChance.dev application.'
    }
  },
  // ── Talent pool ───────────────────────────────────────────────────────
  // The generic inbound door for every source that is not /reentry.
  {
    path: '/join',
    name: 'join',
    component: () => import('@/views/JoinPage.vue'),
    meta: {
      bare: true,
      title: 'Join the Talent Pool — SecondChance.dev',
      description: 'Join the SecondChance.dev pool of experienced people looking for contract work or a full-time role. Free, and we never show your profile to an employer without asking you first.'
    }
  },
  // Tokenised from an email link — no login, because someone on a library
  // computer must be able to change their mind.
  {
    path: '/talent/sharing',
    name: 'talent-sharing',
    component: () => import('@/views/ShareConsentPage.vue'),
    meta: {
      bare: true,
      title: 'Employer introductions — SecondChance.dev',
      description: 'Choose whether SecondChance.dev may introduce you to employers, and change your mind at any time.'
    }
  },
  {
    path: '/admin/talent',
    name: 'admin-talent',
    component: () => import('@/views/AdminTalentPage.vue'),
    meta: {
      title: 'Talent Pool - Admin',
      description: 'Work the pool of applicants and contractors and record referrals.',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/404Page.vue'),
    meta: {
      title: '404 - Page Not Found - SecondChance.dev',
      description: 'The page you are looking for could not be found.'
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    } else {
      return { top: 0 }
    }
  }
})

import { trackPageView } from '@/lib/analytics'

// Helper: wait for Clerk to load (max 5 seconds)
async function waitForClerk() {
  if (!window.Clerk) return false
  if (window.Clerk.loaded) return true
  return new Promise(resolve => {
    const timeout = setTimeout(() => resolve(false), 5000)
    const check = setInterval(() => {
      if (window.Clerk?.loaded) {
        clearInterval(check)
        clearTimeout(timeout)
        resolve(true)
      }
    }, 50)
  })
}

// Update page title and meta tags, handle auth guards
router.beforeEach(async (to, from, next) => {
  // Update document title
  document.title = to.meta.title || 'SecondChance.dev'

  // Update meta description
  const descriptionEl = document.querySelector('meta[name="description"]')
  if (descriptionEl && to.meta.description) {
    descriptionEl.setAttribute('content', to.meta.description)
  }

  // Auth guards — use Clerk directly (no Pinia store dependency in guards)
  if (to.meta.requiresAuth || to.meta.requiresGuest || to.meta.requiresAdmin) {
    await waitForClerk()
    const isSignedIn = !!window.Clerk?.user

    if (to.meta.requiresAuth && !isSignedIn) {
      return next({ name: 'login', query: { redirect: to.fullPath } })
    }

    if (to.meta.requiresGuest && isSignedIn) {
      return next({ name: 'dashboard' })
    }

    if (to.meta.requiresAdmin) {
      const isAdmin = window.Clerk?.user?.publicMetadata?.role === 'admin'
      if (!isAdmin) return next({ name: 'home' })
    }
  }

  next()
})

// Track page views after navigation
router.afterEach((to) => {
  // Track page view in analytics
  trackPageView(to.meta.title || 'SecondChance.dev', window.location.href)
})

export default router